import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMasterAuth } from '@/context/MasterAuthContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Shield } from 'lucide-react';

export default function MasterLoginPage() {
  const navigate = useNavigate();
  const { login, user } = useMasterAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  React.useEffect(() => {
    if (user) navigate('/master');
  }, [user, navigate]);

  const formatApiErrorDetail = (detail) => {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(email, password);
      toast.success('Welcome, Master Admin!');
      navigate('/master');
    } catch (err) {
      const errorMsg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0E0E23] via-[#1A1A3E] to-[#0E0E23] flex flex-col font-sans">
      {/* Background grid */}
      <div className="fixed inset-0 opacity-[0.03] pointer-events-none" style={{
        backgroundImage: 'linear-gradient(rgba(139,92,246,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(139,92,246,0.3) 1px, transparent 1px)',
        backgroundSize: '60px 60px'
      }} />

      <nav className="border-b border-white/5 relative z-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-center h-16 lg:h-20">
            <div className="flex items-center gap-2 text-white">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#3B82F6] flex items-center justify-center shadow-[0_0_15px_rgba(139,92,246,0.4)]">
                <Shield size={18} className="text-white" />
              </div>
              <span className="font-bold text-xl">Master Admin</span>
            </div>
          </div>
        </div>
      </nav>

      <main className="flex-1 flex items-center justify-center px-6 py-12 relative z-10">
        <div className="w-full max-w-md" data-testid="master-login-page">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#8B5CF6] to-[#3B82F6] flex items-center justify-center mx-auto mb-5 shadow-[0_0_30px_rgba(139,92,246,0.4)]">
              <Shield size={32} className="text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white" data-testid="master-login-title">Master Dashboard</h1>
            <p className="text-[#9CA3AF] mt-2">Manage all your client sites</p>
          </div>

          <div className="bg-[#171A2C]/60 backdrop-blur-xl border border-white/5 shadow-[0_8px_32px_0_rgba(0,0,0,0.36)] rounded-2xl p-8">
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="master-login-form">
              <div>
                <Label htmlFor="email" className="text-[#94A3B8] text-sm">Email</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="master@admin.com" required className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl" data-testid="master-login-email" />
              </div>
              <div>
                <Label htmlFor="password" className="text-[#94A3B8] text-sm">Password</Label>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" required className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl" data-testid="master-login-password" />
              </div>

              {error && <p className="text-[#FF6B6B] text-sm" data-testid="master-login-error">{error}</p>}

              <button type="submit" disabled={loading} className="w-full py-3 rounded-xl font-medium text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_15px_rgba(139,92,246,0.4)] hover:shadow-[0_0_25px_rgba(139,92,246,0.6)] transition-all disabled:opacity-50" data-testid="master-login-submit">
                {loading ? 'Signing in...' : 'Access Master Dashboard'}
              </button>
            </form>
          </div>

          <p className="text-center text-sm text-[#64748B] mt-8">
            <Link to="/login" className="hover:text-white transition-colors" data-testid="client-login-link">Client Login</Link>
          </p>
        </div>
      </main>
    </div>
  );
}
